/**
 * Whether a signed-in member may open an admin page for this club.
 *
 * Pure, so the rule behind `middleware/auth.js` can be tested; the middleware
 * only reads the route and calls this.
 *
 * Two things this fixes, both found by the September 2026 compliance run:
 *  - a SUPER admin was bounced off every other club's admin pages to their own
 *    dashboard, because only `adminFor` was consulted and the super flag never
 *    was. The console exists so that a super admin can open any club; being
 *    silently swapped to `/admin` looked like a general deep-link defect and
 *    cost two windows time before it was named.
 *  - `adminFor` holds lowercase GUIDs and the comparison was case-sensitive,
 *    so an upper-case club id in a URL failed the same way. A GUID is not
 *    case-sensitive anywhere else in the product.
 */
export function canOpenClubAdmin(user, clubId) {
  if (!user) return false;
  if (user.superAdmin) return true;
  if (!clubId) return false;
  const wanted = String(clubId).toLowerCase();
  return (user.adminFor || []).some((id) => String(id).toLowerCase() === wanted);
}

/**
 * Whether the admin console's lower menu offers "Switch club".
 *
 * Every club admin, not only those with two or more clubs. The entry opens
 * `/admin?switchClub=true`, the "Your clubs" page, which is also where the
 * "Create a club" card lives; and `redirects.global.js` sends a single-club
 * admin who opens plain `/admin` straight to their club's dashboard, so this
 * entry is the only way such an admin reaches that card from the console.
 * Hiding it for a single club (26 Aug 2026, on the reasoning that there was
 * nothing to switch to) quietly removed club creation from the console for
 * the most common kind of admin: reported by the owner on 25 Sep 2026.
 */
export function canSwitchClub(user) {
  if (!user) return false;
  if (user.superAdmin) return true;
  return (user.adminFor?.length ?? 0) > 0;
}
